// File: frontend/src/components/Navbar.js
// Version: 3.0 (quick-260908-pun окно 1 — навигация fitenko 20 → 9)
// Date: 2026-09-08
// Changelog v3.0: правило видимости и модель меню ВЫНЕСЕНЫ из компонента.
//                 `getUserRoleLabel`, `getRoleColor`, `isAllowed` переехали в чистый
//                 `config/navPermissions.js` (держатель ОДИН, его можно исполнить и
//                 сверить с эталоном). Пять прежних выпадающих больше не пишутся
//                 руками — они СТРОЯТСЯ проекцией `legacyGroups` из общей модели
//                 `config/fitenkoNav.js`, включая иконки (карта ICONS): у MLG и ELN
//                 вид, порядок и иконки прежние. Арендатор fitenko вместо
//                 Dashboard + пяти выпадающих получает ПЛОСКИЙ список разделов
//                 макета; свёрнутое достижимо полосой подраздела (SectionSubnav).
// Changelog v2.1: removed module-scoped _brandingCache + the duplicated network call to
//                 the branding endpoint; now reads from BrandingContext.useBranding()
//                 (single source of truth — Wave 1).
//                 Swept raw Tailwind color classes for theme-token utilities (bg-card, text-foreground,
//                 etc.) per THEME-04. Role pill colors retained as semantic role indicators.

import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { getUserRoleLabel, getRoleColor, isAllowedByKey } from '../config/navPermissions';
import { legacyGroups, fitenkoRail } from '../config/fitenkoNav';
// Колонка арендатора fitenko (quick-260909-qa3). Ветка MLG её не импортирует
// в разметку: рендер стоит под тем же рубежом isFitenko, что и `rail`.
import FitenkoRail, { FitenkoRailHead, FitenkoRailUser } from './FitenkoRail';
// Верхняя панель арендатора fitenko (quick-260911-dzd). Ветка MLG её не
// создаёт: рендер стоит под тем же рубежом isFitenko, что и колонка, а оба
// сетевых вызова живут ВНУТРИ компонента.
import FitenkoVerhnyayaPanel from './FitenkoVerhnyayaPanel';
import { canSeeKatifi, aiName } from '../config/katifiAccess';
import { useNavCounters } from '../hooks/useNavCounters';
import './Navbar.css';
import UiLanguageToggle from './UiLanguageToggle';

// Dropdown component
const NavDropdown = ({ label, icon, items, onLinkClick }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (items.length === 0) return null;

    return (
        <div className="nav-dropdown" ref={ref}>
            <button
                className={`nav-dropdown-trigger text-foreground hover:bg-muted ${open ? 'is-open' : ''}`}
                onClick={() => setOpen(!open)}
            >
                <span className="nav-dropdown-icon">{icon}</span>
                {label}
                <span className="nav-dropdown-arrow text-muted-foreground">{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div className="nav-dropdown-menu bg-popover border-border">
                    {items.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => { setOpen(false); onLinkClick(); }}
                            className={({ isActive }) => `nav-dropdown-item text-foreground hover:bg-accent ${isActive ? 'active nav-item--active' : ''}`}
                        >
                            <span className="nav-dropdown-item-icon">{item.icon}</span>
                            {item.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
};

const Navbar = () => {
    const { user, navLinks, logout, isAuthenticated, loading } = useAuth();
    // Phase 07.1 Wave 2: consolidated /api/v1/branding access via BrandingContext.
    // Replaces previous local fetch + module-scoped _brandingCache (3rd duplicate fetch
    // across Login.js / Home.js / Navbar.js — now single source of truth via Wave 1).
    const { branding, isFitenko } = useBranding();
    // 🔴 Хук зовётся БЕЗУСЛОВНО (правило хуков React), а рубеж арендатора стоит
    //    ВНУТРИ него: при !isFitenko он не делает ни одного сетевого вызова.
    const counters = useNavCounters();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const brandName = ((branding?.instance_name || 'mlg').toUpperCase()) + ' OS';

    const handleLogout = () => { logout(); setIsMenuOpen(false); };
    const handleLinkClick = () => { setIsMenuOpen(false); };

    if (loading || !isAuthenticated) return null;

    const userDisplayName = user?.full_name || user?.first_name || user?.email || 'User';
    const userRole = getUserRoleLabel(user?.roles);
    const roleColors = getRoleColor(userRole);

    // Пять прежних групп СТРОЯТСЯ фильтром по `group` из общей модели — их метки,
    // порядок, пути и иконки те же, что были зашиты здесь до quick-260908-pun.
    const groups = legacyGroups(user, navLinks);
    const businessItems = groups.business;
    const aiItems = groups.ai;
    const seoItems = groups.seo;
    const toolsItems = groups.tools;
    const adminItems = groups.admin;

    // 🔴 Девять разделов макета строятся ТОЛЬКО для fitenko. MLG и ELN этого не
    //    заказывали, и безусловный вызов подменил бы им меню.
    const rail = isFitenko ? fitenkoRail(user, navLinks) : null;   // РУБЕЖ АРЕНДАТОРА

    return (
        // Phase 07.1 Wave 2: layer shadcn tokens onto existing CSS classes.
        // bg-card maps to white (MLG) / cream-deep (Fitenko); text-foreground maps to
        // slate-900 (MLG) / ink (Fitenko). CSS class names still own layout/positioning.
        <nav className="main-navbar bg-card text-foreground border-b border-border">
            <NavLink to="/dashboard" className="navbar-brand text-primary" onClick={handleLinkClick}>
                {isFitenko ? <FitenkoRailHead tenant={branding?.company_name} /> : brandName}
            </NavLink>

            <button
                className={`menu-toggle ${isMenuOpen ? 'is-open' : ''}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
            >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
            </button>

            <div className={`navbar-links bg-card ${isMenuOpen ? 'is-open' : ''}`}>
                {rail ? (
                    <FitenkoRail sections={rail} counters={counters} onLinkClick={handleLinkClick} />
                ) : (
                    <>
                        <NavLink
                            to="/dashboard"
                            onClick={handleLinkClick}
                            className={({ isActive }) => `text-foreground hover:bg-muted ${isActive ? 'active nav-item--active' : ''}`}
                        >
                            <span style={{ marginRight: '4px' }}>🏠</span> Dashboard
                        </NavLink>

                        <NavDropdown label="Business" icon="💼" items={businessItems} onLinkClick={handleLinkClick} />
                        <NavDropdown label="AI" icon="🤖" items={aiItems} onLinkClick={handleLinkClick} />
                        <NavDropdown label="SEO" icon="📈" items={seoItems} onLinkClick={handleLinkClick} />
                        {toolsItems.length > 0 && <NavDropdown label="Tools" icon="🛠️" items={toolsItems} onLinkClick={handleLinkClick} />}
                        {adminItems.length > 0 && <NavDropdown label="Admin" icon="⚙️" items={adminItems} onLinkClick={handleLinkClick} />}
                    </>
                )}
            </div>

            {/* 🔴 ВЕРХНЯЯ ПАНЕЛЬ (quick-260911-dzd) — СЛОЙ: она попадает на КАЖДЫЙ
                экран арендатора сразу, потому что живёт в общей оболочке.
                Рубеж тот же `isFitenko`, под которым строится колонка: MLG, ELN
                и ELFNY не получают ни одного её узла — ни разметки, ни сетевых
                вызовов (оба запроса живут ВНУТРИ компонента, а он не создаётся).
                Порядок узлов взят из атласа: поиск · офис · «Спросить …».
                Языка и уведомлений в панели НЕТ — причина в шапке компонента. */}
            {isFitenko && <FitenkoVerhnyayaPanel />}

            {isFitenko && canSeeKatifi(user) && (
                <button
                    type="button"
                    className="fk-topbar-ask"
                    onClick={() => window.dispatchEvent(new CustomEvent('katifi:open'))}
                >
                    Спросить {aiName(user)}
                </button>
            )}

            <div className={`navbar-user-section text-muted-foreground${isFitenko ? ' fk-rail-user' : ''}`}>
                <UiLanguageToggle />
                <NavLink
                    to="/profile"
                    className="user-profile-link text-foreground"
                    onClick={handleLinkClick}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
                >
                    {isFitenko ? (
                        // 🔴 Блок НЕ ВЫНЕСЕН из разметки — он тот же самый, лишь
                        //    обогащён под рубежом. Перенос задел бы MLG.
                        <FitenkoRailUser name={userDisplayName} role={userRole} />
                    ) : (
                        <>
                        <span style={{ fontWeight: '600' }}>{userDisplayName}</span>
                        {userRole && (
                        // keep: semantic — role pill colors encode role identity (Creator=violet,
                        // Partner=amber, Admin=red, Attorney=blue, Paralegal=emerald), not theme.
                        <span style={{
                            fontSize: '0.7rem',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: roleColors.bg,
                            color: roleColors.text,
                            border: `1px solid ${roleColors.border}`,
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            lineHeight: '1'
                        }}>
                            {userRole}
                        </span>
                        )}
                        </>
                    )}
                </NavLink>
                <button onClick={handleLogout} className="logout-button bg-muted hover:bg-primary hover:text-primary-foreground">Logout</button>
            </div>
        </nav>
    );
};

export default Navbar;
